/**
 * Ana ekran: zaman dilimi -> onaylanmis AL/SAT sinyalleri.
 *
 * Uc saniye kurali: kullanici uygulamayi actiginda once zaman dilimini,
 * sonra dogrudan sinyalleri gorur. Grafik ve indikator ayrintisi burada
 * YOKTUR; detay ekranina aittir.
 *
 * SAHTE SINYAL GOSTERILMEZ: liste yalnizca backend'in urettigi onaylanmis
 * sinyalleri gosterir. Veri yoksa bunu acikca yazar. Mini grafik de ayni
 * kurala tabidir: backend gercek kapanis gondermediyse CIZILMEZ.
 */

import { memo, useEffect, useMemo, useState } from "react";
import type { SignalRow, SignalScan } from "../api/types";
import { ago, compact, dateTime, pct, price } from "../lib/format";
import { sinceSignalPct } from "../lib/signal";
import {
  Banner,
  Change,
  CoinAvatar,
  Empty,
  IconArrowDownRight,
  IconArrowUpRight,
  IconCalendar,
  IconClock,
  IconScan,
  IconStar,
  IconTrendDown,
  IconTrendUp,
  Kpi,
  Pill,
  QualityRing,
  SideBadge,
  Sparkline,
  Stars,
} from "./Ui";

export type SideFilter = "all" | "buy" | "sell";

const TIMEFRAME_LABEL: Record<string, string> = {
  "15m": "15 Dakikalık",
  "30m": "30 Dakikalık",
  "45m": "45 Dakikalık",
  "1h": "1 Saatlik",
  "2h": "2 Saatlik",
  "4h": "4 Saatlik",
  "1d": "Günlük",
  "1w": "Haftalık",
};

export function timeframeTitle(timeframe: string): string {
  return `${TIMEFRAME_LABEL[timeframe] ?? timeframe.toUpperCase()} Sinyaller`;
}

/** Zaman dilimi secici - bolumlu (segmented) kontrol. */
export function TimeframeChips({
  timeframes,
  value,
  onChange,
}: {
  timeframes: string[];
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div className="segmented" role="tablist" aria-label="Zaman dilimi">
      {timeframes.map((timeframe) => {
        return (
          <button
            key={timeframe}
            type="button"
            role="tab"
            className="segment"
            aria-pressed={timeframe === value}
            aria-selected={timeframe === value}
            onClick={() => onChange(timeframe)}
          >
            {timeframe.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}

export function ScanTitle({ timeframe }: { timeframe: string }) {
  return <h2 className="page-title">{timeframeTitle(timeframe)}</h2>;
}

const WS_LABEL: Record<string, string> = {
  CONNECTED: "Canlı akış",
  CONNECTING: "Akışa bağlanıyor",
  DISCONNECTED: "Akış kapalı",
  ERROR: "Akış hatası",
  DISABLED: "Akış yok",
};

export function ScanStatus({ scan, loading }: { scan: SignalScan | null; loading: boolean }) {
  const live = scan?.websocket_status === "CONNECTED";
  return (
    <div className="status-line">
      <span className="live" data-state={loading || live ? "live" : "idle"}>
        <span className="pulse" />
        {loading ? "Taranıyor" : (WS_LABEL[scan?.websocket_status ?? ""] ?? "Tarama")}
      </span>
      {scan ? (
        <>
          <span className="status-sep" aria-hidden="true" />
          {/* Sinyaller yalnizca bar kapaninca degisir; tazelik olcusu
              taramanin saati degil, SON KAPANAN BARDIR. */}
          <span className="status-item" title="Son kapanan bar">
            <IconCalendar />
            {/* Dar telefonlarda (<=400px) bu on ek gizlenir: takvim ikonu
                ayni anlami tasiyor ve satirin ikiye bolunmesini onler. */}
            <span className="hide-on-narrow">Son kapanan bar: </span>
            {dateTime(scan.last_close_time)}
          </span>
          <span className="status-sep" aria-hidden="true" />
          <span>
            {scan.scanned}/{scan.universe_size} sembol
          </span>
          {scan.source === "MOCK" ? <span className="down">MOCK VERİ</span> : null}
        </>
      ) : null}
    </div>
  );
}

/**
 * Piyasa taramasinin ozeti - kullanici asagi kaydirmadan tarama durumunu
 * anlayabilsin diye. Yalnizca zaten yuklu `rows`/`counts` uzerinden
 * TUREYEN degerlerdir; ek API cagrisi yapmaz, sahte veri icermez.
 */
export function ScanKpis({
  scan,
  rows,
  counts,
  side,
  onSideChange,
}: {
  scan: SignalScan | null;
  rows: SignalRow[];
  counts: { buy: number; sell: number };
  /** Verilirse ilk uc kart (Toplam/AL/Sat) TIKLANARAK yon filtresi olarak kullanilir. */
  side?: SideFilter;
  onSideChange?: (next: SideFilter) => void;
}) {
  const top = useMemo(
    () =>
      rows.reduce<SignalRow | null>((best, row) => {
        if (!best || row.quote_volume_24h > best.quote_volume_24h) return row;
        return best;
      }, null),
    [rows],
  );

  if (!scan) return null;

  const total = rows.length;
  const buyPct = total > 0 ? (counts.buy / total) * 100 : 0;
  const sellPct = total > 0 ? (counts.sell / total) * 100 : 0;
  const clickable = Boolean(onSideChange);

  return (
    <div className="kpi-grid scan-kpis">
      <Kpi
        label="Toplam Sinyal"
        value={String(total)}
        sub={`${scan.scanned}/${scan.universe_size} sembol`}
        icon={<IconScan />}
        tone="info"
        onClick={clickable ? () => onSideChange?.("all") : undefined}
        active={side === "all"}
      />
      <Kpi
        label="Yükseliş Sinyali"
        value={String(counts.buy)}
        sub={total > 0 ? `%${buyPct.toFixed(1)}` : undefined}
        icon={<IconTrendUp />}
        tone="up"
        onClick={clickable ? () => onSideChange?.("buy") : undefined}
        active={side === "buy"}
      />
      <Kpi
        label="Düşüş Sinyali"
        value={String(counts.sell)}
        sub={total > 0 ? `%${sellPct.toFixed(1)}` : undefined}
        icon={<IconTrendDown />}
        tone="down"
        onClick={clickable ? () => onSideChange?.("sell") : undefined}
        active={side === "sell"}
      />
      <Kpi
        label="En Çok İşlem Gören"
        value={top ? top.symbol : "—"}
        sub={top ? `${compact(top.quote_volume_24h)} hacim` : undefined}
        icon={<IconStar />}
        tone="warn"
      />
      <Kpi
        label="Son Güncelleme"
        value={dateTime(scan.last_close_time)}
        sub="Son kapanan bar"
        icon={<IconClock />}
        tone="info"
      />
    </div>
  );
}

/** Siralanabilir kolonlar. Fiyat kolonlari YOKTUR - farkli coinlerin ham
 *  fiyatlarini karsilastirmak anlamsizdir. */
type SortKey = "symbol" | "change" | "volume" | "quality";
type SortState = { key: SortKey; dir: "asc" | "desc" } | null;

/** Ilk tiklamada hangi yon: degisim/hacim/kalitede once BUYUK, sembolde A-Z. */
const FIRST_DIR: Record<SortKey, "asc" | "desc"> = {
  symbol: "asc",
  change: "desc",
  volume: "desc",
  quality: "desc",
};

const SORT_HINT: Record<SortKey, { asc: string; desc: string }> = {
  symbol: { asc: "A'dan Z'ye", desc: "Z'den A'ya" },
  change: { asc: "en çok düşen önce", desc: "en çok artan önce" },
  volume: { asc: "en düşük hacim önce", desc: "en yüksek hacim önce" },
  quality: { asc: "düşük kaliteli önce", desc: "yüksek kaliteli önce" },
};

function SortIcon({ dir }: { dir: "asc" | "desc" | null }) {
  return (
    <svg className="th-arrow" viewBox="0 0 10 14" width="8" height="11" aria-hidden="true">
      {dir !== "desc" ? <path d="M5 1 L9 5.5 H1 Z" fill="currentColor" /> : null}
      {dir !== "asc" ? <path d="M5 13 L9 8.5 H1 Z" fill="currentColor" /> : null}
    </svg>
  );
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  className,
}: {
  label: string;
  sortKey: SortKey;
  sort: SortState;
  onSort: (key: SortKey) => void;
  className?: string;
}) {
  const active = sort && sort.key === sortKey ? sort : null;
  const hint = active
    ? SORT_HINT[sortKey][active.dir]
    : "sıralamak için tıkla · üçüncü tıklama varsayılana döner";
  return (
    <span className={className}>
      <button
        type="button"
        className="th-sort"
        data-active={active ? "true" : undefined}
        onClick={() => onSort(sortKey)}
        title={`${label} — ${hint}`}
        aria-label={`${label} — ${hint}`}
      >
        {label}
        {/* Metin karakteri (↕ ▲ ▼) DEGIL cizim: telefon ve tabletler o
            karakterleri mavi emoji kutusu olarak ciziyordu. */}
        <SortIcon dir={active ? active.dir : null} />
      </button>
    </span>
  );
}

export function SignalList({
  rows,
  loading,
  onSelect,
  emptyTitle,
  emptyDetail,
  timeframe,
  holdings = [],
  onToggleFavorite,
  tradeShorts = false,
}: {
  rows: SignalRow[];
  timeframe: string;
  loading: boolean;
  onSelect: (symbol: string) => void;
  emptyTitle: string;
  emptyDetail?: string;
  /** Elimde var işaretli semboller - SAT sinyalini çıkış noktası olarak vurgular. */
  holdings?: string[];
  /** Verilmezse satırdaki yıldız gösterilmez (ör. salt okunur listeler). */
  onToggleFavorite?: (symbol: string) => void;
  /** Vadeli listesi: SHORT gercek islemdir - "cikis referansi" soluklastirmasi yapilmaz. */
  tradeShorts?: boolean;
}) {
  /** null = backend sirasi: EN YENI SINYAL once (yas, sonra hacim). */
  const [sort, setSort] = useState<SortState>(null);
  /** Kac satir CIZILIR. Arama/filtre/siralama TUM listeye uygulanir; bu
   *  yalnizca ekrana basilan satir sayisidir (bkz. PAGE_SIZE). */
  const [shown, setShown] = useState(PAGE_SIZE);
  useEffect(() => {
    setShown(PAGE_SIZE);
  }, [timeframe]);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((left, right) => {
      if (sort.key === "symbol") {
        return factor * left.symbol.localeCompare(right.symbol, "tr");
      }
      if (sort.key === "quality") {
        // Kalite hesaplanamamis satirlar HER ZAMAN en sona duser (yon fark etmez) -
        // "hesaplanamadi" bir deger DEGIL, siralamada anlamsiz bir bosluktur.
        const a = left.quality_score;
        const b = right.quality_score;
        if (a == null && b == null) return 0;
        if (a == null) return 1;
        if (b == null) return -1;
        return factor * (a - b);
      }
      // "Sinyalden" sutunu artik sinyalden bu yana (canli) degisimi gosterir;
      // siralama da gorulen degere gore.
      const a = sort.key === "change" ? sinceSignalPct(left) : left.quote_volume_24h;
      const b = sort.key === "change" ? sinceSignalPct(right) : right.quote_volume_24h;
      return factor * (a - b);
    });
  }, [rows, sort]);

  // Ucuncu tiklama siralamayi KALDIRIR - varsayilan "en yeni sinyal" donusu.
  const cycle = (key: SortKey) =>
    setSort((current) => {
      if (!current || current.key !== key) return { key, dir: FIRST_DIR[key] };
      if (current.dir === FIRST_DIR[key]) {
        return { key, dir: current.dir === "desc" ? "asc" : "desc" };
      }
      return null;
    });

  if (loading && rows.length === 0) {
    return (
      <div className="signal-list">
        {[0, 1, 2, 3, 4].map((index) => (
          <div key={index} className="skeleton" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return <Empty title={emptyTitle} detail={emptyDetail} />;
  }

  return (
    <div className="table-shell" data-kind="scan">
      {/* Telefon: kolon basliklari gizli - degisime gore siralama tek dugmede
          (kullanici 12.10). Ayni `sort` durumunu paylasir. */}
      <div className="mobile-sort">
        <button
          type="button"
          className="mobile-sort-btn"
          data-active={sort?.key === "change" ? "true" : undefined}
          onClick={() => cycle("change")}
        >
          Değişim
          <SortIcon dir={sort?.key === "change" ? sort.dir : null} />
        </button>
        <span className="mobile-sort-hint">
          {sort?.key === "change" ? SORT_HINT.change[sort.dir] : "en yeni sinyal önce"}
        </span>
      </div>
      <div className="signal-head">
        <span aria-hidden="true" />
        <span className="col-rank">#</span>
        <SortHeader label="Sembol" sortKey="symbol" sort={sort} onSort={cycle} />
        <span>Yön</span>
        <span title="Sinyalin verildiği fiyat (sabit)">Sinyal Fiyatı</span>
        <SortHeader label="Sinyalden" sortKey="change" sort={sort} onSort={cycle} />
        <span className="only-xwide">Mini Grafik (24 bar)</span>
        <SortHeader
          label="Hacim (24s)"
          sortKey="volume"
          sort={sort}
          onSort={cycle}
          className="only-xwide"
        />
        <SortHeader label="Kalite" sortKey="quality" sort={sort} onSort={cycle} />
        <span className="only-xwide">Son Sinyal</span>
        <span className="align-end">İşlem</span>
      </div>
      <div className="signal-list">
        {sorted.slice(0, shown).map((row, index) => (
          <SignalItem
            key={`${row.symbol}:${row.signal_close_time}`}
            rank={index + 1}
            row={row}
            timeframe={timeframe}
            onSelect={onSelect}
            held={holdings.includes(row.symbol)}
            onToggleFavorite={onToggleFavorite}
            tradeShorts={tradeShorts}
          />
        ))}
      </div>
      {sorted.length > shown ? (
        <button
          type="button"
          className="btn list-more"
          onClick={() => setShown((current) => current + PAGE_SIZE)}
        >
          Daha fazla göster ({sorted.length - shown} sinyal daha)
        </button>
      ) : null}
    </div>
  );
}

/**
 * Ekrana ayni anda basilan satir sayisi. 400 satirin hepsi (mini grafik +
 * kalite halkasi + logo) her 5 sn'lik yoklamada yeniden cizildiginde, giris
 * seviyesi bir tablette ekran her seferinde ~0.6-0.9 sn donuyordu (CPU 6x
 * yavaslatilmis olcum). Kullanici listenin ustune bakar; gerisi istenince.
 */
const PAGE_SIZE = 50;

/** Iki satir ayni mi - yoklama her seferinde YENI nesneler getirir, bu
 *  yuzden kimlik degil DEGER karsilastirilir (diziler/nesneler dahil). */
function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, index) => sameValue(item, b[index]));
  }
  if (a && b && typeof a === "object" && typeof b === "object") {
    const left = a as Record<string, unknown>;
    const right = b as Record<string, unknown>;
    const keys = Object.keys(left);
    return (
      keys.length === Object.keys(right).length &&
      keys.every((key) => sameValue(left[key], right[key]))
    );
  }
  return false;
}

/** Degismeyen satir yeniden CIZILMEZ: 5 sn'lik yoklamada yalnizca fiyati,
 *  grafigi, yasi vb. degisen satirlar cizilir. */
const SignalItem = memo(SignalItemView, (prev, next) =>
  prev.rank === next.rank &&
  prev.held === next.held &&
  prev.timeframe === next.timeframe &&
  prev.onSelect === next.onSelect &&
  prev.onToggleFavorite === next.onToggleFavorite &&
  prev.tradeShorts === next.tradeShorts &&
  sameValue(prev.row, next.row),
);

function SignalItemView({
  rank,
  row,
  timeframe,
  onSelect,
  held,
  onToggleFavorite,
  tradeShorts = false,
}: {
  rank: number;
  row: SignalRow;
  timeframe: string;
  onSelect: (symbol: string) => void;
  held: boolean;
  onToggleFavorite?: (symbol: string) => void;
  tradeShorts?: boolean;
}) {
  /* SAT sinyalleri islem acmak icin degildir (spot-only, kisa pozisyon yok) -
     yalnizca "elimde var" isaretli bir coin icin CIKIS noktasidir. Isaretli
     degilse sinyal yine gorunur ama referans oldugunu belli etmek icin
     soluklastirilir; isaretliyse "Cikis" rozetiyle vurgulanir. */
  const isExitReference = row.side === "SELL" && !held && !tradeShorts;
  const isExitAlert = row.side === "SELL" && held && !tradeShorts;
  /* Mini grafik rengi CIZILEN serinin kendi yonunden gelir (ilk -> son
     kapanis), sinyal yonunden degil - dusen bir grafik yesil gorunmesin. */
  const spark = row.spark;
  const first = spark[0];
  const last = spark[spark.length - 1];
  const sparkTone = first != null && last != null && last < first ? "down" : "up";

  return (
    <div
      className="signal"
      data-side={row.side}
      data-muted={isExitReference ? "true" : undefined}
      onClick={() => onSelect(row.symbol)}
    >
      <span className="cell cell-star only-wide">
        {onToggleFavorite ? (
          <button
            type="button"
            className="row-icon"
            data-active={held ? "true" : "false"}
            onClick={(event) => {
              event.stopPropagation();
              onToggleFavorite(row.symbol);
            }}
            aria-label={
              held
                ? `${row.symbol} elimde yok olarak işaretle`
                : `${row.symbol} elimde var olarak işaretle`
            }
          >
            <IconStar filled={held} />
          </button>
        ) : null}
      </span>

      <span className="cell cell-rank only-wide">{rank}</span>

      <span className="cell cell-symbol">
        <CoinAvatar symbol={row.symbol} base={row.base_asset} />
        <span className="cell-symbol-text">
          <span className="signal-symbol-row">
            {/* Klavyeyle erisim satirin kendisinden degil bu dugmeden saglanir. */}
            <button
              type="button"
              className="signal-symbol"
              onClick={(event) => {
                event.stopPropagation();
                onSelect(row.symbol);
              }}
              aria-label={
                isExitAlert
                  ? `${row.symbol} ${row.side_label} sinyali - elinizdeki coin için çıkış noktası, detayı aç`
                  : `${row.symbol} ${row.side_label} sinyali, detayı aç`
              }
            >
              {row.symbol}
            </button>
            {isExitAlert ? <Pill tone="warn">Çıkış</Pill> : null}
            {row.quality_score != null ? (
              <span className="only-narrow">
                <Stars score={row.quality_score} max={row.quality_max} />
              </span>
            ) : null}
            {row.is_high_quality ? (
              <Pill tone="up" title="4h + Yükseliş için doğrulanmış kalite filtresini geçti (kazanma %68.2)">
                ★
              </Pill>
            ) : null}
            {row.full_confluence ? (
              <Pill
                tone="info"
                title="Supertrend, EMA Crossover, Donchian Breakout ve MACD'nin dördü de aynı anda yukarı trend diyor (araştırmayla doğrulandı: train %+0.61, test %+0.52)"
              >
                Tam Uyum
              </Pill>
            ) : null}
          </span>
          <span className="signal-meta">
            {row.base_asset} · {timeframe.toUpperCase()} ·{" "}
            <span title={`Sinyal onaylandıktan sonra ${row.age_bars} bar kapandı`}>
              {row.age_bars} bar
            </span>
          </span>
        </span>
      </span>

      <span className="cell cell-side">
        <SideBadge side={row.side} label={row.side_label} />
      </span>

      {/* Dar ekran: SINYAL fiyati (sabit) + sinyalden bu yana (canli).
          Anlik fiyat listede DEGIL, coin detayinin en ustundedir - liste
          "sinyal nerede verildi, o gunden beri ne oldu" sorusunu yanitlar. */}
      <span
        className="cell cell-num align-end only-narrow"
        title={`Sinyal fiyatı ${price(row.signal_price)}`}
      >
        <span className="signal-price">{price(row.signal_price)}</span>
        <span className="signal-change">
          <Change value={sinceSignalPct(row)} />
        </span>
      </span>

      {/* Sinyalin verildigi fiyat - SABIT. */}
      <span className="cell cell-num only-wide">
        <span className="signal-price">{price(row.signal_price)}</span>
        <span className="signal-meta">{row.quote_asset}</span>
      </span>

      {/* Buyuk ve renkli: sinyalden bu yana (canli fiyatla, her 5 sn).
          Altta sonuk: 24 saatlik degisim (borsa ozetinden, daha seyrek). */}
      <span className="cell cell-num only-wide" title={`Sinyal fiyatı ${price(row.signal_price)}`}>
        <span className="signal-change">
          <Change value={sinceSignalPct(row)} />
        </span>
        <span className="signal-meta since-change">24s {pct(row.change_24h_pct)}</span>
      </span>

      <span className="cell cell-spark only-xwide">
        <Sparkline points={spark} tone={sparkTone} />
      </span>

      <span className="cell cell-num only-xwide">
        <span className="signal-price">{compact(row.quote_volume_24h)}</span>
        <span className="signal-meta">{row.quote_asset}</span>
      </span>

      <span className="cell cell-quality only-wide">
        <QualityRing score={row.quality_score} max={row.quality_max} />
      </span>

      <span className="cell cell-num only-xwide" title={`Sinyal fiyatı ${price(row.signal_price)}`}>
        <span className="signal-when">{ago(row.signal_close_time)}</span>
        <span className="signal-meta">{dateTime(row.signal_close_time)}</span>
      </span>

      <span className="cell cell-actions only-wide">
        {/* GORSEL aksiyon: emir GONDERMEZ - detay ekranini acar (spot-only,
            otomatik islem yok). Davranis satir tiklamasiyla ayni. */}
        <button
          type="button"
          className="row-action"
          data-side={row.side}
          onClick={(event) => {
            event.stopPropagation();
            onSelect(row.symbol);
          }}
          aria-label={`${row.symbol} ${row.side_label} - detayını aç`}
          title={row.side_label}
        >
          {row.side === "BUY" ? <IconArrowUpRight /> : <IconArrowDownRight />}
        </button>
      </span>
    </div>
  );
}

export function ScanErrors({ scan }: { scan: SignalScan | null }) {
  if (!scan || scan.errors.length === 0) return null;
  return (
    <Banner>
      {scan.errors.length} sembol taranamadı (veri alınamadı): {scan.errors
        .slice(0, 3)
        .map((error) => error.symbol)
        .join(", ")}
      {scan.errors.length > 3 ? "…" : ""}
    </Banner>
  );
}
